





























































import {
    Component,
    Vue,
    Watch,
} from 'vue-property-decorator';
import StatsCard from '@/components/GlobalPanorama/StatsCard.vue';
import QueueRow from '@/components/GlobalPanorama/QueueRow.vue';
import Spinner from '@/atomics/loaders/Spinner.vue';
import { Agent } from '@/store/modules/agent';
import idealName from '@/utils/ideal-name';
import { urlApi } from '@/utils/url';
import { getUserInfo } from '@/store/plugins/userInfoPlugin';

@Component({
    components: {
        StatsCard,
        QueueRow,
        Spinner,
    },
})
export default class GlobalPanorama extends Vue {
    queueDids: Record<string, string[]> = {};

    isLoadingDids = false;

    didsFetched = false;

    // Helper: extraer extensión numérica del nombre del agente
    // eslint-disable-next-line class-methods-use-this
    getExtension(agentName: string): string {
        // Primero intentar extraer dígitos antes del @
        const extensionMatch = agentName.match(/^(\d+)@/);
        if (extensionMatch) {
            return extensionMatch[1];
        }
        // Fallback: usar idealName
        return idealName(agentName);
    }

    // Helper: verificar si agente está en llamada
    // eslint-disable-next-line class-methods-use-this
    isAgentInCall(agent: Agent): boolean {
        if (agent.state === 'in_queue_call') return true;
        if (agent.queueCalls && agent.queueCalls.size > 0) return true;
        if (agent.callStates && agent.callStates.size > 0) return true;
        return false;
    }

    // Agentes en llamada (únicos por extensión)
    get agentsInCallCount(): number {
        const agentsContainer = this.$store.state.agent?.agentsContainer || {};
        const extensionsInCall = new Set<string>();

        Object.values(agentsContainer).forEach((agent: unknown) => {
            const a = agent as Agent;
            if (a.connected && this.isAgentInCall(a)) {
                extensionsInCall.add(this.getExtension(a.name));
            }
        });

        return extensionsInCall.size;
    }

    // Llamadas en espera: suma de membersCount de todas las queues
    get waitingCallsCount(): number {
        let total = 0;
        this.queueNames.forEach((queueName: string) => {
            total += this.$store.getters.queueMembersCount(queueName) || 0;
        });
        return total;
    }

    // Agentes en linea (únicos por extensión, sin importar estado)
    get agentsConnectedCount(): number {
        const agentsContainer = this.$store.state.agent?.agentsContainer || {};
        const connectedExtensions = new Set<string>();

        Object.values(agentsContainer).forEach((agent: unknown) => {
            const a = agent as Agent;
            if (a.connected) {
                connectedExtensions.add(this.getExtension(a.name));
            }
        });

        return connectedExtensions.size;
    }

    get queueNames(): string[] {
        return this.$store.getters.queueNames() || [];
    }

    // eslint-disable-next-line class-methods-use-this
    get customerId(): number | null {
        const userInfo = getUserInfo();
        if (!userInfo || !userInfo.customers || !userInfo.currentDomain) {
            return null;
        }
        const customer = userInfo.customers.find(
            (c) => c.domain === userInfo.currentDomain,
        );
        return customer?.id || null;
    }

    mounted() {
        // Try to fetch immediately if data is ready
        this.fetchAllDids();

        // Also listen for userInfo loaded event
        this.$root.$on('currentDomain', () => {
            if (!this.didsFetched) {
                this.fetchAllDids();
            }
        });
    }

    beforeDestroy() {
        this.$root.$off('currentDomain');
    }

    @Watch('queueNames.length')
    onQueuesChange() {
        // Refetch DIDs when queues change (if not already fetching)
        if (!this.isLoadingDids && this.queueNames.length > 0 && !this.didsFetched) {
            this.fetchAllDids();
        }
    }

    // eslint-disable-next-line class-methods-use-this
    getQueueKey(name: string): string {
        return idealName(name);
    }

    async fetchAllDids() {
        if (this.queueNames.length === 0 || this.customerId === null) {
            return;
        }

        this.isLoadingDids = true;

        try {
            const queueNamesForApi = this.queueNames.map((name) => idealName(name));
            const url = `${urlApi}/queues/dids`;
            const token = sessionStorage.getItem('token') || '';

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    customerId: this.customerId,
                    queueNames: queueNamesForApi,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${JSON.stringify(data)}`);
            }

            // data format: { "7001": ["351..."], "7002": ["352..."] }
            this.queueDids = data;
            this.didsFetched = true;
        } catch (err) {
            // Error silently handled - DIDs will just be empty
        } finally {
            this.isLoadingDids = false;
        }
    }
}
